import { useEffect, useMemo, useState } from 'react';
import Head from 'next/head';
import TextField from '@mui/material/TextField';
import SearchIcon from '@mui/icons-material/Search';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import { Controller, useForm } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { useSitecoreContext } from '@sitecore-jss/sitecore-jss-nextjs';
import { isEditorActive } from '@sitecore-jss/sitecore-jss-nextjs/utils';
import { getSearchBox } from '../../lib/utils/coveo/searchBoxes';
import { setContext } from '../../lib/utils/coveo/headlessContext';
import { setDayOfBirth, setZipCode } from '../../features/coveoSlice';
import { RootState } from '../../store/store';
import SearchFormInput from '../HeroAndSearch/SearchFormInput';
import { HeroAndSearchType } from '../HeroAndSearch/HeroAndSearch.types';
import ButtonPrimary from '../shared/Button/PrimaryButton';
import {
  StBarCard,
  StBarOuter,
  StBarRow,
  StField,
  StInputShell,
  StLabel,
  StSubmitWrap,
} from './HomeSearchBar.styles';

const HomeSearchBar = (props: HeroAndSearchType): JSX.Element => {
  const { sitecoreContext }: any = useSitecoreContext();
  const dispatch = useDispatch();
  const zipValue = useSelector((state: RootState) => state.coveoResult.zipCode);
  const userProfile = useSelector((state: RootState) => state.crProfile);
  const profileDetails = userProfile?.profileData?.basicDetails;
  const [localZipcode, setLocalZipcode] = useState<string>('');

  useEffect(() => {
    if (isEditorActive() || sitecoreContext?.pageState === 'preview') {
      return;
    }
    const userInterests = userProfile?.profileData?.conditions?.map(
      (item: any) => item.conditionValue
    );
    if (profileDetails) {
      setContext({
        gender: profileDetails?.gender,
        dateOfBirth: profileDetails?.dob,
        interests: userInterests ? userInterests : [],
      });
    } else {
      setContext({
        gender: '',
        dateOfBirth: '',
        interests: '',
      });
    }
  }, [profileDetails?.dob, profileDetails?.gender, userProfile?.profileData?.conditions]);

  useEffect(() => {
    if (profileDetails?.dob) {
      dispatch(setDayOfBirth(String(new Date(profileDetails?.dob).getFullYear())));
    }
  }, [dispatch, profileDetails?.dob]);

  // --- copied from SearchForm.tsx: standalone Coveo search box ---
  const searchBox = useMemo(
    () =>
      getSearchBox(
        props?.fields?.totalSuggestion?.value || 20,
        props?.fields?.searchlink?.value?.href || '/find-trials/search-result'
      ),
    []
  );

  const coiRequiredErrorMessage = props.fields?.coiRequiredErrorMessage?.value || '';
  const isCOIRequired = props.fields?.isCOIRequired?.value || '';
  const isZipcodeRequired = props.fields?.isZipcodeRequired?.value || '';
  const zipcodeRegex = props.fields?.zipcodeRegex?.value || '';
  const zipcodeRegexFailedErrorMessage = props.fields?.zipcodeRegexFailedErrorMessage?.value || '';
  const zipcodeRequiredErrorMessage = props.fields?.zipcodeRequiredErrorMessage?.value || '';

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<any>({
    defaultValues: { 'hero-and-search-coi': '', 'hero-and-search-zipcode': '' },
    mode: 'all',
  });
  const onZipCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.value.length > 5) return;
    dispatch(setZipCode(e.target.value));
    setLocalZipcode(e.target.value);
    setValue('hero-and-search-zipcode', e.target.value);
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const subscribe = searchBox.subscribe(() => {
      const { redirectTo, value, analytics } = searchBox.state;

      if (redirectTo) {
        const data = JSON.stringify({ value, analytics });
        localStorage.setItem('coveo_standalone_search_box_data', data);
        searchBox.afterRedirection();
        window.location.href = redirectTo;
      }
    });

    return () => subscribe();
  }, [searchBox]);

  useEffect(() => {
    if (profileDetails?.zipCode && !zipValue) {
      localStorage.setItem('zipCode', profileDetails.zipCode);
    } else {
      localStorage.setItem('zipCode', zipValue);
    }
  }, [profileDetails?.zipCode, zipValue]);

  const coveoSearchSubmit = () => {
    if (errors['hero-and-search-coi'] || errors['hero-and-search-zipcode']) {
      return;
    }
    searchBox.submit();
  };

  const generateSearchFormSchema = () => {
    const searchFormSchema = {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      url: (sitecoreContext?.siteSettings as any)?.baseUrl?.value,
      name: sitecoreContext?.site?.name,
      potentialAction: {
        '@type': 'SearchAction',
        target: `${
          (sitecoreContext?.siteSettings as any)?.baseUrl?.value
        }/find-trials/search-result#q={search_term_string}`,
        'query-input': 'required name=search_term_string',
      },
    };
    return (
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(searchFormSchema),
          }}
        />
      </Head>
    );
  };

  if (!props?.fields) return <></>;

  return (
    <StBarOuter
      id="home-search-bar"
      className="ui-home-search-bar"
      style={{ background: props.fields.bgColor?.value || undefined }}
    >
      {generateSearchFormSchema()}
      <StBarCard>
        <StBarRow onSubmit={handleSubmit(coveoSearchSubmit)}>
          <StField>
            <StLabel>{props.fields.conditionOfInterestLabel?.value || 'CONDITION'}</StLabel>
            <StInputShell>
              <SearchIcon
                className="ui-home-search-bar__condition-icon"
                fontSize="small"
                aria-hidden
              />
              <Controller
                name="hero-and-search-coi"
                control={control}
                defaultValue={''}
                rules={{
                  required: {
                    value: isCOIRequired,
                    message: coiRequiredErrorMessage,
                  },
                  validate: (value) => {
                    if (isCOIRequired && typeof value === 'string' && value?.trim() === '') {
                      return coiRequiredErrorMessage;
                    }
                    return true;
                  },
                }}
                render={({ field }) => (
                  <SearchFormInput
                    {...field}
                    searchBox={searchBox}
                    fieldID="hero-and-search-coi"
                    placeholder={props?.fields?.conditionOfInterestPlaceholder?.value}
                    coiInputTextLimit={props?.fields?.coiInputTextLimit?.value || 50}
                    lengthToSuggestion={props?.fields?.lengthToSuggestion?.value || 3}
                    errors={errors}
                    setValue={setValue}
                  />
                )}
              />
            </StInputShell>
          </StField>
          <StField>
            <StLabel>{props.fields.zipCodeLabel?.value || 'LOCATION'}</StLabel>
            <StInputShell>
              <PlaceOutlinedIcon
                className="ui-home-search-bar__location-icon"
                fontSize="small"
                aria-hidden
              />
              <Controller
                name="hero-and-search-zipcode"
                control={control}
                rules={{
                  required: {
                    value: isZipcodeRequired,
                    message: zipcodeRequiredErrorMessage,
                  },
                  pattern: {
                    value: new RegExp(zipcodeRegex),
                    message: zipcodeRegexFailedErrorMessage,
                  },
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    id="hero-and-search-zipcode"
                    size="small"
                    placeholder={props?.fields?.zipCodePlaceholder?.value}
                    value={localZipcode}
                    inputProps={{
                      'aria-label': 'zipcode',
                      'aria-required': isZipcodeRequired || false,
                    }}
                    onChange={onZipCodeChange}
                    error={Boolean(errors?.['hero-and-search-zipcode'])}
                    helperText={String(
                      (errors && errors?.['hero-and-search-zipcode']?.message) || ''
                    )}
                  />
                )}
              />
            </StInputShell>
          </StField>
          <StSubmitWrap>
            <ButtonPrimary
              id="home-search-bar-submit"
              className="ui-button-contained ui-home-search-bar__submit"
              type="submit"
            >
              {props?.fields?.link?.value?.text}
            </ButtonPrimary>
          </StSubmitWrap>
        </StBarRow>
      </StBarCard>
    </StBarOuter>
  );
};
export default HomeSearchBar;
